/**
 * R2 Advanced CA: Hooks for Change Orders API.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChangeOrderItem {
  id?: string;
  description: string;
  quantity: number;
  unit_price: number;
  amount?: number;
  sort_order?: number;
}

export interface ChangeOrderCreateData {
  title: string;
  description?: string | null;
  notes?: string | null;
  items?: ChangeOrderItem[];
}

export interface ChangeOrderUpdateData {
  title?: string;
  description?: string | null;
  notes?: string | null;
  items?: ChangeOrderItem[];
}

export interface ReviewRequest {
  notes?: string | null;
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useChangeOrders(projectId: string | undefined) {
  return useQuery({
    queryKey: ['change-orders', projectId],
    queryFn: () => api.changeOrders.list(projectId!),
    enabled: !!projectId,
    retry: 2,
  });
}

export function useChangeOrder(coId: string | undefined) {
  return useQuery({
    queryKey: ['change-order', coId],
    queryFn: () => api.changeOrders.get(coId!),
    enabled: !!coId,
  });
}

export function useChangeOrderImpact(projectId: string | undefined) {
  return useQuery({
    queryKey: ['change-order-impact', projectId],
    queryFn: () => api.changeOrders.impact(projectId!),
    enabled: !!projectId,
    retry: 2,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useCreateChangeOrder(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ChangeOrderCreateData) => api.changeOrders.create(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['change-orders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['change-order-impact', projectId] });
      toast.success('Change order created');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create change order');
    },
  });
}

export function useUpdateChangeOrder(projectId: string, coId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ChangeOrderUpdateData) => api.changeOrders.update(coId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['change-orders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['change-order', coId] });
      toast.success('Change order updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update change order');
    },
  });
}

export function useSubmitChangeOrder(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (coId: string) => api.changeOrders.submit(coId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['change-orders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['change-order-impact', projectId] });
      toast.success('Change order submitted for approval');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to submit change order');
    },
  });
}

export function useApproveChangeOrder(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ coId, notes }: { coId: string; notes?: string }) =>
      api.changeOrders.approve(coId, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['change-orders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['change-order-impact', projectId] });
      queryClient.invalidateQueries({ queryKey: ['sov', projectId] });
      toast.success('Change order approved and added to SOV');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to approve change order');
    },
  });
}

export function useRejectChangeOrder(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ coId, notes }: { coId: string; notes?: string }) =>
      api.changeOrders.reject(coId, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['change-orders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['change-order-impact', projectId] });
      toast.success('Change order rejected');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to reject change order');
    },
  });
}
